import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import baseURL from '../utils/baseURL';
import useAxiosAuth from '../hooks/useAxiosAuth';
import { FEATURE_KEYS, isFeatureEnabled } from './featureSlice';

const extractError = (error) =>
    error.response?.data?.message || error.message || 'Request failed';

// ---- Subscription packages (admin CRUD) ----

export const getSubscriptionPackages = createAsyncThunk(
    'subscription/getPackages',
    async (_, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const res = await axiosInstance.get(`${baseURL}/subscription-package`);
            return res.data.packages || [];
        } catch (error) {
            return rejectWithValue(extractError(error));
        }
    }
);

// PUBLIC — active packages for the pre-login landing page (no auth required).
export const getPublicSubscriptionPackages = createAsyncThunk(
    'subscription/getPublicPackages',
    async (_, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const res = await axiosInstance.get(`${baseURL}/subscription-package/public`);
            return res.data.packages || [];
        } catch (error) {
            return rejectWithValue(extractError(error));
        }
    }
);

export const createSubscriptionPackage = createAsyncThunk(
    'subscription/createPackage',
    async (payload, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const res = await axiosInstance.post(`${baseURL}/subscription-package`, payload);
            return res.data.package;
        } catch (error) {
            return rejectWithValue(extractError(error));
        }
    }
);

export const updateSubscriptionPackage = createAsyncThunk(
    'subscription/updatePackage',
    async ({ id, ...payload }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const res = await axiosInstance.put(`${baseURL}/subscription-package/${id}`, payload);
            return res.data.package;
        } catch (error) {
            return rejectWithValue(extractError(error));
        }
    }
);

export const deleteSubscriptionPackage = createAsyncThunk(
    'subscription/deletePackage',
    async (id, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            await axiosInstance.delete(`${baseURL}/subscription-package/${id}`);
            return id;
        } catch (error) {
            return rejectWithValue(extractError(error));
        }
    }
);

// Persist the admin's drag order. `order` is an array of package ids; the backend
// writes each id's index as its sortOrder and returns the re-sorted list, which is
// then shown identically on the admin + doctor sides.
export const reorderSubscriptionPackages = createAsyncThunk(
    'subscription/reorderPackages',
    async (order, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const res = await axiosInstance.put(`${baseURL}/subscription-package/reorder`, { order });
            return res.data.packages || [];
        } catch (error) {
            return rejectWithValue(extractError(error));
        }
    }
);

// ---- Doctor ↔ package link + resolved access ----

// Admin assigns (or clears) a doctor's package. packageId null/'' = unassign.
export const assignDoctorPackage = createAsyncThunk(
    'subscription/assignDoctorPackage',
    async ({ doctorId, packageId, period }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const res = await axiosInstance.put(`${baseURL}/subscription-package/doctor/${doctorId}`, { packageId, period });
            return res.data; // { doctorId, packageId, pending, ... }
        } catch (error) {
            return rejectWithValue(extractError(error));
        }
    }
);

// The doctor's raw package assignment ({ doctorId, packageId, isActive }).
export const getDoctorPackage = createAsyncThunk(
    'subscription/getDoctorPackage',
    async (doctorId, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const res = await axiosInstance.get(`${baseURL}/subscription-package/doctor/${doctorId}`);
            return res.data;
        } catch (error) {
            return rejectWithValue(extractError(error));
        }
    }
);

// The doctor's RESOLVED access ({ packageId, packageName, controls }). Use this
// to gate UI: controls is { [controlId]: true | '<value>' }.
export const getDoctorAccess = createAsyncThunk(
    'subscription/getDoctorAccess',
    async (doctorId, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const res = await axiosInstance.get(`${baseURL}/subscription-package/doctor/${doctorId}/access`);
            return res.data;
        } catch (error) {
            return rejectWithValue(extractError(error));
        }
    }
);

// ---- Subscription lifecycle (trial / expiry / renew / grace) ----

// Resolved status: { packageId, packageName, price, durationDays, isFree, status,
// accessAllowed, daysLeft, startDate, endDate, graceUntil, freeUsed, controls }.
export const getSubscriptionStatus = createAsyncThunk(
    'subscription/getStatus',
    async (doctorId, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const res = await axiosInstance.get(`${baseURL}/subscription-package/doctor/${doctorId}/status`);
            return res.data;
        } catch (error) {
            return rejectWithValue(extractError(error));
        }
    }
);

// REQUEST → INVOICE → ACTIVATE (owner, 22 Sep 2026 — Q8). There is no payment
// gateway: the card checkout that used to live here is gone, endpoint included,
// because a platform that takes no money must not keep a route that activates a
// plan on a fake card. A doctor asks for a plan, the platform e-mails an invoice,
// and an admin (tenant or Super Admin) marks it paid.
//
// The period was never sent to the old checkout — the server then priced the
// default period, which is not the one the doctor clicked. It is sent here.
export const requestSubscription = createAsyncThunk(
    'subscription/request',
    async ({ doctorId, packageId, period }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const res = await axiosInstance.post(`${baseURL}/subscription-package/doctor/${doctorId}/request`, { packageId, period });
            return res.data; // { payment, status } — status is a fresh computeStatus
        } catch (error) {
            return rejectWithValue(extractError(error));
        }
    }
);

// Admin / Super Admin: the money arrived outside the platform (UPI, bank
// transfer, cash) — record the reference and switch the plan on.
export const activateSubscription = createAsyncThunk(
    'subscription/activate',
    async ({ doctorId, reference, method, note }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const res = await axiosInstance.post(`${baseURL}/subscription-package/doctor/${doctorId}/activate`, { reference, method, note });
            return res.data; // { payment, status }
        } catch (error) {
            return rejectWithValue(extractError(error));
        }
    }
);

// Admin / Super Admin refuses a request. ADMIN-ONLY on the server — a doctor
// calling it gets a 403, which is why My Subscription shows no "cancel" button.
export const declineSubscription = createAsyncThunk(
    'subscription/decline',
    async ({ doctorId, reason }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const res = await axiosInstance.post(`${baseURL}/subscription-package/doctor/${doctorId}/decline`, { reason });
            return res.data; // { payment, status }
        } catch (error) {
            return rejectWithValue(extractError(error));
        }
    }
);

// Admin grants N grace days to an expired doctor.
export const grantGrace = createAsyncThunk(
    'subscription/grantGrace',
    async ({ doctorId, days }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const res = await axiosInstance.put(`${baseURL}/subscription-package/doctor/${doctorId}/grace`, { days });
            return res.data; // { status }
        } catch (error) {
            return rejectWithValue(extractError(error));
        }
    }
);

export const getDoctorPayments = createAsyncThunk(
    'subscription/getPayments',
    async (doctorId, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const res = await axiosInstance.get(`${baseURL}/subscription-package/doctor/${doctorId}/payments`);
            return res.data.payments || [];
        } catch (error) {
            return rejectWithValue(extractError(error));
        }
    }
);

// Admin: subscription status for every doctor (for the Doctors table flags).
export const getDoctorsStatuses = createAsyncThunk(
    'subscription/getDoctorsStatuses',
    async (_, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const res = await axiosInstance.get(`${baseURL}/subscription-package/doctors/statuses`);
            return res.data.statuses || [];
        } catch (error) {
            return rejectWithValue(extractError(error));
        }
    }
);

// ---- Access helpers (use in components to gate features) ----
// A control is "on" if it's a boolean true OR a non-empty value-type string.
export const hasControl = (controls, id) => {
    const v = controls?.[id];
    return v !== undefined && v !== false && v !== '';
};
export const controlValue = (controls, id) => controls?.[id];

// Package controls that ALSO have an existing global admin feature flag. For
// these, effective access = the doctor's package grants it AND the admin's
// global flag is on (so neither side alone can enable a hidden feature).
// patient_library's global-flag pairing removed with the Patient Library page
// (India-SaaS decisions log #18), and the plan control itself on 1 Oct 2026
// (owner) along with `dashboard`. Old packages may still hold either key in
// their JSON; nothing reads them.
const CONTROL_TO_FEATURE_KEY = {};

// canUse(state, controlId): the single source of truth for gating a feature.
// Combines the doctor's resolved package access with any global feature flag.
export const canUse = (state, controlId) => {
    const granted = hasControl(state.subscription?.doctorAccess?.controls, controlId);
    if (!granted) return false;
    const fk = CONTROL_TO_FEATURE_KEY[controlId];
    return fk ? isFeatureEnabled(state, fk) : true;
};

// Patient-limit helpers — work off a doctor's controls (own doctorAccess.controls
// or a selected doctor's planControls) + their current active patient count.
export const patientLimitValue = (controls) => controlValue(controls, 'patient_limit');
export const patientLimitReached = (controls, count) => {
    const v = patientLimitValue(controls);
    if (!v || String(v).toLowerCase() === 'unlimited') return false;
    const lim = parseInt(v, 10);
    return Number.isFinite(lim) && Number(count || 0) >= lim;
};
export const patientLimitMessage = (controls) => {
    const v = patientLimitValue(controls);
    return `Patient limit reached${v ? ` (${v})` : ''}. Upgrade your subscription plan to add more patients.`;
};

// Tooltip shown when a doctor's plan doesn't include video consultations.
export const SECURED_VIDEO_MSG = 'Video consultations are not included in this plan. Upgrade the subscription to enable video.';

const subscriptionSlice = createSlice({
    name: 'subscription',
    initialState: {
        packages: [],
        publicPackages: [],
        loading: false,
        error: null,
        saving: false,
        // Doctor access (filled by getDoctorAccess). `controls` drives gating.
        doctorAccess: { packageId: null, packageName: null, controls: {} },
        doctorPackage: { packageId: null, isActive: false },
        accessLoading: false,
        assigning: false,
        // Lifecycle (filled by getSubscriptionStatus).
        mySubscription: null,
        statusLoading: false,
        checkingOut: false,
        payments: [],
        // Admin Doctors table: { [doctorId]: { status, accessAllowed, daysLeft } }.
        doctorsStatuses: {},
    },
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(getSubscriptionPackages.pending, (state) => { state.loading = true; state.error = null; })
            .addCase(getSubscriptionPackages.fulfilled, (state, action) => { state.loading = false; state.packages = action.payload; })
            .addCase(getSubscriptionPackages.rejected, (state, action) => { state.loading = false; state.error = action.payload; })

            .addCase(getPublicSubscriptionPackages.fulfilled, (state, action) => { state.publicPackages = action.payload; })

            // Reorder returns the freshly sorted list — keep the store in sync.
            .addCase(reorderSubscriptionPackages.fulfilled, (state, action) => {
                if (Array.isArray(action.payload) && action.payload.length) state.packages = action.payload;
            })

            .addCase(createSubscriptionPackage.pending, (state) => { state.saving = true; })
            .addCase(createSubscriptionPackage.fulfilled, (state, action) => {
                state.saving = false;
                if (action.payload) state.packages.push(action.payload);
            })
            .addCase(createSubscriptionPackage.rejected, (state) => { state.saving = false; })

            .addCase(updateSubscriptionPackage.pending, (state) => { state.saving = true; })
            .addCase(updateSubscriptionPackage.fulfilled, (state, action) => {
                state.saving = false;
                const updated = action.payload;
                if (updated) state.packages = state.packages.map((p) => (p.id === updated.id ? updated : p));
            })
            .addCase(updateSubscriptionPackage.rejected, (state) => { state.saving = false; })

            .addCase(deleteSubscriptionPackage.fulfilled, (state, action) => {
                state.packages = state.packages.filter((p) => p.id !== action.payload);
            })

            // Doctor ↔ package link + resolved access
            .addCase(getDoctorAccess.pending, (state) => { state.accessLoading = true; })
            .addCase(getDoctorAccess.fulfilled, (state, action) => {
                state.accessLoading = false;
                state.doctorAccess = action.payload || { packageId: null, packageName: null, controls: {} };
            })
            .addCase(getDoctorAccess.rejected, (state) => { state.accessLoading = false; })

            .addCase(getDoctorPackage.fulfilled, (state, action) => {
                state.doctorPackage = {
                    packageId: action.payload?.packageId ?? null,
                    isActive: action.payload?.isActive ?? false,
                };
            })

            .addCase(assignDoctorPackage.pending, (state) => { state.assigning = true; })
            .addCase(assignDoctorPackage.fulfilled, (state, action) => {
                state.assigning = false;
                // isActive tracks packageId: a package was assigned vs unassigned.
                const pkgId = action.payload?.packageId || null;
                state.doctorPackage = { packageId: pkgId, isActive: !!pkgId };
                if (action.payload?.access) state.doctorAccess = action.payload.access;
            })
            .addCase(assignDoctorPackage.rejected, (state) => { state.assigning = false; })

            // Lifecycle
            .addCase(getSubscriptionStatus.pending, (state) => { state.statusLoading = true; })
            .addCase(getSubscriptionStatus.fulfilled, (state, action) => {
                state.statusLoading = false;
                state.mySubscription = action.payload;
            })
            .addCase(getSubscriptionStatus.rejected, (state) => { state.statusLoading = false; })

            // All three answer { payment, status } — `status` is a fresh
            // computeStatus, so the store takes it whole, and the payment row
            // joins the history the doctor sees (pending after a request, paid
            // after an activation).
            .addCase(requestSubscription.pending, (state) => { state.checkingOut = true; })
            .addCase(requestSubscription.fulfilled, (state, action) => {
                state.checkingOut = false;
                if (action.payload?.status) state.mySubscription = action.payload.status;
                if (action.payload?.payment) state.payments.unshift(action.payload.payment);
            })
            .addCase(requestSubscription.rejected, (state) => { state.checkingOut = false; })

            .addCase(activateSubscription.pending, (state) => { state.checkingOut = true; })
            .addCase(activateSubscription.fulfilled, (state, action) => {
                state.checkingOut = false;
                if (action.payload?.status) state.mySubscription = action.payload.status;
            })
            .addCase(activateSubscription.rejected, (state) => { state.checkingOut = false; })

            .addCase(declineSubscription.pending, (state) => { state.checkingOut = true; })
            .addCase(declineSubscription.fulfilled, (state, action) => {
                state.checkingOut = false;
                if (action.payload?.status) state.mySubscription = action.payload.status;
            })
            .addCase(declineSubscription.rejected, (state) => { state.checkingOut = false; })

            .addCase(grantGrace.fulfilled, (state, action) => {
                if (action.payload?.status) state.mySubscription = action.payload.status;
            })

            .addCase(getDoctorPayments.fulfilled, (state, action) => { state.payments = action.payload; })

            .addCase(getDoctorsStatuses.fulfilled, (state, action) => {
                const map = {};
                (action.payload || []).forEach((s) => { map[s.doctorId] = s; });
                state.doctorsStatuses = map;
            });
    },
});

// True when the doctor's plan has expired and no grace remains → block the app.
export const isSubscriptionLocked = (state) => {
    const s = state.subscription?.mySubscription;
    return !!s && s.accessAllowed === false;
};

export default subscriptionSlice.reducer;
